import { computed, ref, type Ref } from 'vue';
import { useContactsSearch } from '@/composables/useContactsSearch';
import { useCompanyUsers } from '@/composables/useCompanyUsers';
import type { EmailAddress } from '@/types';

/** Atributo que marca una mención en el cuerpo; al enviar se leen de aquí los correos. */
export const MENTION_ATTR = 'data-mention-email';
const MAX_CANDIDATES = 8;

/** Sin tildes y en minúsculas: "jose" encuentra a "José". */
const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Lo escrito tras la @ cuando el cursor está al final de una mención en curso,
 * o null si no hay ninguna. La @ tiene que ir al inicio o tras un espacio, para
 * que escribir un correo (ana@sky.com) no abra la lista.
 */
export function detectMentionQuery(textBeforeCaret: string): string | null {
  const match = textBeforeCaret.match(/(?:^|\s)@([^\s@]*)$/);
  return match ? match[1] : null;
}

/**
 * Personas que coinciden con lo escrito (nombre o correo), sin repetidas por
 * correo en minúsculas y en el orden de las fuentes: la primera aparición gana.
 */
export function buildMentionCandidates(query: string, sources: EmailAddress[][], limit = MAX_CANDIDATES): EmailAddress[] {
  const q = fold(query.trim());
  const seen = new Set<string>();
  const result: EmailAddress[] = [];
  for (const person of sources.flat()) {
    const email = person?.email?.trim() || '';
    const key = email.toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (q && !key.includes(q) && !fold(person.name || '').includes(q)) continue;
    result.push({ email, name: person.name || email });
    if (result.length >= limit) break;
  }
  return result;
}

/** Correos de las menciones que siguen en el cuerpo, sin repetidos. */
export function extractMentionEmails(html: string): string[] {
  // DOMParser no ejecuta nada ni carga imágenes, a diferencia de un div con innerHTML.
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  const emails = Array.from(doc.querySelectorAll(`[${MENTION_ATTR}]`))
    .map((el) => (el.getAttribute(MENTION_ATTR) || '').trim().toLowerCase())
    .filter(Boolean);
  return [...new Set(emails)];
}

/**
 * "Para" con la persona al final si no está ya en Para, CC ni CCO (como
 * Outlook); null si ya estaba. Arreglo nuevo, no push: la lista de arrastre
 * solo recalcula cuando cambia la referencia.
 */
export function addToRecipients(
  recipients: { to: EmailAddress[]; cc?: EmailAddress[]; bcc?: EmailAddress[] },
  person: EmailAddress
): EmailAddress[] | null {
  const key = person.email.toLowerCase();
  const all = [...recipients.to, ...(recipients.cc || []), ...(recipients.bcc || [])];
  if (all.some((r) => r.email.toLowerCase() === key)) return null;
  return [...recipients.to, person];
}

/**
 * Menú de menciones sobre un contenteditable: escribir @ abre la lista y al
 * elegir a alguien la @ con lo escrito se reemplaza por una ficha no editable.
 */
export function useMentions(
  editorRef: Ref<HTMLElement | null>,
  options: {
    /** Destinatarios del borrador y participantes del correo original */
    participants: () => EmailAddress[];
    onSelect: (person: EmailAddress) => void;
    /** Se llama tras modificar el DOM del editor, que no dispara `input` */
    onChange: () => void;
  }
) {
  const contacts = useContactsSearch();
  const companyPeople = ref<EmailAddress[]>([]);
  let companyRequested = false;

  const isOpen = ref(false);
  const query = ref('');
  const highlightedIndex = ref(0);
  const menuStyle = ref<Record<string, string>>({});
  // Nodo de texto y posición de la @ que se va a reemplazar
  let anchor: { node: Text; start: number } | null = null;

  const candidates = computed(() =>
    isOpen.value
      ? buildMentionCandidates(query.value, [
          options.participants(),
          companyPeople.value,
          contacts.results.value.map((c) => contacts.contactToEmailAddress(c)),
        ])
      : []
  );

  async function loadCompanyUsers() {
    if (companyRequested) return;
    companyRequested = true;
    try {
      const { users, fetchUsers } = useCompanyUsers();
      await fetchUsers();
      companyPeople.value = users.value
        .filter((u) => u.email)
        .map((u) => ({ email: u.email, name: `${u.name} ${u.lastName}`.trim() || u.email }));
    } catch {
      // Sin compañía o sin permiso de leer usuarios: la lista sigue con participantes y contactos
      companyRequested = false;
    }
  }

  function close() {
    if (!isOpen.value && !anchor) return;
    isOpen.value = false;
    anchor = null;
    query.value = '';
    contacts.resetSearch();
  }

  function positionMenu(range: Range) {
    const rect = typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect() : null;
    // Un rango colapsado puede venir en ceros (inicio de línea): se usa el editor
    const box = rect && (rect.top || rect.left) ? rect : editorRef.value?.getBoundingClientRect();
    if (!box) return;
    const left = Math.max(8, Math.min(box.left, window.innerWidth - 288));
    menuStyle.value = { top: `${box.bottom + 4}px`, left: `${left}px` };
  }

  /** Va en el @input del editor: abre, filtra o cierra según lo que haya tras el cursor. */
  function onInput() {
    const selection = window.getSelection();
    const node = selection?.anchorNode;
    if (!selection || !selection.isCollapsed || !node || node.nodeType !== Node.TEXT_NODE || !editorRef.value?.contains(node)) {
      close();
      return;
    }
    const offset = selection.anchorOffset;
    const found = detectMentionQuery((node.textContent || '').slice(0, offset));
    if (found === null) {
      close();
      return;
    }
    anchor = { node: node as Text, start: offset - found.length - 1 };
    if (!isOpen.value) void loadCompanyUsers();
    query.value = found;
    highlightedIndex.value = 0;
    isOpen.value = true;
    contacts.search(found);
    if (selection.rangeCount > 0) positionMenu(selection.getRangeAt(0));
  }

  function select(person: EmailAddress) {
    const selection = window.getSelection();
    if (!anchor || !selection || !anchor.node.isConnected) {
      close();
      return;
    }
    const { node, start } = anchor;
    const end = selection.anchorNode === node ? selection.anchorOffset : start + 1 + query.value.length;

    const range = document.createRange();
    range.setStart(node, start);
    range.setEnd(node, Math.min(Math.max(end, start), node.length));
    range.deleteContents();

    const mention = document.createElement('span');
    mention.setAttribute(MENTION_ATTR, person.email);
    mention.setAttribute('contenteditable', 'false');
    mention.className = 'font-semibold text-primary-700 dark:text-brand-mint';
    mention.textContent = `@${person.name || person.email}`;
    // El espacio deja el cursor fuera de la ficha para seguir escribiendo
    const space = document.createTextNode(' ');
    range.insertNode(space);
    range.insertNode(mention);

    range.setStartAfter(space);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    close();
    options.onChange();
    options.onSelect(person);
  }

  /** Va en el @keydown del editor: flechas, Enter/Tab para elegir y Escape para cerrar. */
  function onKeydown(event: KeyboardEvent) {
    if (!isOpen.value) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    const list = candidates.value;
    if (list.length === 0) return;
    const current = Math.min(highlightedIndex.value, list.length - 1);
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      highlightedIndex.value = (current + 1) % list.length;
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      highlightedIndex.value = (current - 1 + list.length) % list.length;
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      select(list[current]);
    }
  }

  return {
    isOpen,
    query,
    candidates,
    highlightedIndex,
    menuStyle,
    onInput,
    onKeydown,
    select,
    close,
  };
}
